/**
 * Profile Page
 *
 * Allows users to view and edit their profile information.
 */

import React, { useState } from 'react';
import { Navbar, Card, Input, Button } from '../components';
import { useAuthStore } from '../store/authStore';
import { authApi, getErrorMessage } from '../services/api';

export const ProfilePage: React.FC = () => {
  const { user, setUser } = useAuthStore();
  const [name, setName] = useState(user?.name || '');
  const [photo, setPhoto] = useState(user?.photo || '');
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setSuccessMessage('');
    setErrorMessage('');

    try {
      const updatedUser = await authApi.updateProfile({ name, photo });
      setUser(updatedUser);
      setSuccessMessage('Profile updated successfully!');
    } catch (error) {
      setErrorMessage(getErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  };

  const pageStyles: React.CSSProperties = {
    minHeight: '100vh',
    backgroundColor: 'var(--bg-primary)',
  };

  const containerStyles: React.CSSProperties = {
    maxWidth: '800px',
    margin: '0 auto',
    padding: '40px 20px',
  };

  const titleStyles: React.CSSProperties = {
    fontSize: '32px',
    fontWeight: 600,
    marginBottom: '32px',
    color: 'var(--text-primary)',
  };

  const formStyles: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  };

  const sectionStyles: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  };

  const sectionTitleStyles: React.CSSProperties = {
    fontSize: '16px',
    fontWeight: 600,
    color: 'var(--text-primary)',
  };

  const infoTextStyles: React.CSSProperties = {
    fontSize: '14px',
    color: 'var(--text-secondary)',
  };

  const buttonGroupStyles: React.CSSProperties = {
    display: 'flex',
    gap: '12px',
    marginTop: '16px',
  };

  const avatarPreviewStyles: React.CSSProperties = {
    width: '100px',
    height: '100px',
    borderRadius: '50%',
    objectFit: 'cover',
    border: '2px solid var(--border-color)',
  };

  const messageStyles = (type: 'success' | 'error'): React.CSSProperties => ({
    padding: '12px 16px',
    borderRadius: '8px',
    fontSize: '14px',
    backgroundColor: type === 'success' ? '#e8f5e9' : '#fce4ec',
    color: type === 'success' ? '#2e7d32' : '#c62828',
    border: `1px solid ${type === 'success' ? '#c8e6c9' : '#f8bbd0'}`,
    // Note: Success/error colors remain fixed for visibility
  });

  if (!user) return null;

  return (
    <div style={pageStyles}>
      <Navbar />

      <div style={containerStyles}>
        <h1 style={titleStyles}>Profile Settings</h1>

        <Card>
          <form onSubmit={handleSubmit} style={formStyles}>
            {/* Avatar Preview */}
            <div style={sectionStyles}>
              <div style={sectionTitleStyles}>Profile Picture</div>
              {(photo || user.photo) && (
                <img
                  src={photo || user.photo}
                  alt="Profile"
                  style={avatarPreviewStyles}
                />
              )}
              <Input
                label="Photo URL"
                type="url"
                value={photo}
                onChange={(e) => setPhoto(e.target.value)}
                placeholder="https://example.com/photo.jpg"
                fullWidth
              />
              <p style={infoTextStyles}>
                Enter a URL to your profile picture. For best results, use a square image.
              </p>
            </div>

            {/* Name */}
            <div style={sectionStyles}>
              <Input
                label="Display Name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your Name"
                fullWidth
                required
              />
            </div>

            {/* Email (Read-only) */}
            <div style={sectionStyles}>
              <Input
                label="Email Address"
                type="email"
                value={user.email}
                disabled
                fullWidth
              />
              <p style={infoTextStyles}>
                Email address is managed by your Google account and cannot be changed here.
              </p>
            </div>

            {/* Account Info (Read-only) */}
            <div style={sectionStyles}>
              <div style={sectionTitleStyles}>Account Information</div>
              <div style={infoTextStyles}>
                <strong>Role:</strong> {user.role}<br />
                <strong>Status:</strong> {user.status}<br />
                <strong>Member since:</strong> {new Date(user.createdAt).toLocaleDateString()}<br />
                {user.lastLoginAt && (
                  <>
                    <strong>Last login:</strong> {new Date(user.lastLoginAt).toLocaleString()}
                  </>
                )}
              </div>
            </div>

            {/* Success/Error Messages */}
            {successMessage && (
              <div style={messageStyles('success')}>{successMessage}</div>
            )}
            {errorMessage && (
              <div style={messageStyles('error')}>{errorMessage}</div>
            )}

            {/* Buttons */}
            <div style={buttonGroupStyles}>
              <Button
                type="submit"
                variant="primary"
                isLoading={isLoading}
              >
                Save Changes
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={() => window.history.back()}
              >
                Cancel
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
};
